import type { Language } from '@/contexts/LanguageContext';

type AnalyticsTheme = 'light' | 'dark';
type EventParams = Record<string, unknown>;
type QueueItem =
  | { type: 'page_view'; path: string }
  | { type: 'event'; name: string; params: EventParams };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_ID = (import.meta as ImportMeta).env?.VITE_GA_ID?.trim();
const IS_PROD_BUILD = (import.meta as ImportMeta).env?.PROD === true;
const PROD_HOST_PATTERN = /(^|\.)unfollowedstats\.com$/i;
const SCRIPT_LOAD_TIMEOUT_MS = 8000;
const MAX_QUEUE_ITEMS = 200;

let initialized = false;
let scriptLoadPromise: Promise<void> | null = null;
let initPromise: Promise<void> | null = null;
let disabledByInitError = false;
let currentLanguage: Language = 'en';
let currentTheme: AnalyticsTheme = 'light';
let lastTrackedPagePath: string | null = null;
let queuedItems: QueueItem[] = [];
let isFlushingQueue = false;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const canTrack = () => {
  if (disabledByInitError) return false;
  if (!IS_PROD_BUILD) return false;
  if (!isBrowser()) return false;
  if (!GA_ID) return false;
  return PROD_HOST_PATTERN.test(window.location.hostname);
};

const buildCommonParams = (): EventParams => ({
  language: currentLanguage,
  theme: currentTheme
});

const injectGtagScript = () => {
  if (!canTrack()) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID as string)}`;
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`);
    let timeoutId: number | null = null;

    const finish = (callback: () => void) => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      callback();
    };

    timeoutId = window.setTimeout(() => {
      reject(new Error('ga_script_load_timeout'));
    }, SCRIPT_LOAD_TIMEOUT_MS);

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        finish(resolve);
        return;
      }
      existingScript.addEventListener('load', () => {
        existingScript.dataset.loaded = 'true';
        finish(resolve);
      });
      existingScript.addEventListener('error', () =>
        finish(() => reject(new Error('ga_script_load_failed')))
      );
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      finish(resolve);
    });
    script.addEventListener('error', () =>
      finish(() => reject(new Error('ga_script_load_failed')))
    );
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

const ensureGtag = () => {
  if (!canTrack()) return false;

  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }

  return true;
};

const updateUserProperties = () => {
  if (!initialized || !canTrack() || !window.gtag) return;
  window.gtag('set', 'user_properties', buildCommonParams());
};

const emitPageView = (path: string) => {
  if (!canTrack() || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    ...buildCommonParams()
  });
};

const emitEvent = (name: string, params: EventParams = {}) => {
  if (!canTrack() || !window.gtag) return;

  window.gtag('event', name, {
    ...buildCommonParams(),
    ...params
  });
};

const flushQueuedItems = () => {
  if (!initialized || !window.gtag || queuedItems.length === 0) return;
  if (isFlushingQueue) return;

  isFlushingQueue = true;
  const itemsToFlush = queuedItems;
  queuedItems = [];
  try {
    itemsToFlush.forEach((item) => {
      if (item.type === 'page_view') {
        emitPageView(item.path);
        return;
      }
      emitEvent(item.name, item.params);
    });
  } finally {
    isFlushingQueue = false;
  }
};

const enqueueItem = (item: QueueItem) => {
  if (queuedItems.length >= MAX_QUEUE_ITEMS) {
    queuedItems.shift();
  }
  queuedItems.push(item);
};

export const initAnalytics = async () => {
  if (!canTrack() || initialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      if (!ensureGtag()) return;

      // Follow the official gtag bootstrap sequence: queue commands first,
      // then load gtag.js which consumes dataLayer.
      window.gtag?.('js', new Date());
      window.gtag?.('config', GA_ID, {
        send_page_view: false,
        anonymize_ip: true,
        transport_type: 'beacon'
      });

      await injectGtagScript();

      initialized = true;
      updateUserProperties();
      flushQueuedItems();
    } catch (error) {
      disabledByInitError = true;
      queuedItems = [];
      console.warn('GA4 initialization failed', error);
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
};

const requestInitIfNeeded = () => {
  if (!canTrack() || initialized || initPromise) {
    return;
  }
  void initAnalytics();
};

export const setAnalyticsLanguage = (language: Language) => {
  currentLanguage = language;
  updateUserProperties();
};

export const setAnalyticsTheme = (theme: AnalyticsTheme) => {
  currentTheme = theme;
  updateUserProperties();
};

export const trackPageView = (path: string) => {
  if (!canTrack()) return;

  const normalizedPath = path || '/';
  if (lastTrackedPagePath === normalizedPath) return;
  lastTrackedPagePath = normalizedPath;

  if (!initialized || !window.gtag) {
    enqueueItem({ type: 'page_view', path: normalizedPath });
    requestInitIfNeeded();
    return;
  }

  emitPageView(normalizedPath);
};

export const trackEvent = (name: string, params: EventParams = {}) => {
  if (!canTrack()) return;

  if (!initialized || !window.gtag) {
    enqueueItem({ type: 'event', name, params });
    requestInitIfNeeded();
    return;
  }

  emitEvent(name, params);
};
